import dayjs from "dayjs";
import { monthValues } from "@core/old_helpers";
import { iLTOChart, YearSeasonsEnum } from "@models/LTOChart";
import { LaunchActivityMonth } from "@apiHooks/useCategoryProfileLaunches";
import { LAUNCHES_SEASON_ORDER } from "./Launches.constants";

export interface LaunchesChartBar {
    id: string;
    value: number;
    season?: YearSeasonsEnum;
    month?: string;
}

export interface LaunchesChartCell {
    year: number;
    items: LaunchesChartBar[];
}

type LaunchesChartRow = LaunchesChartBar & { year: number };

const SEASONS_PER_YEAR = LAUNCHES_SEASON_ORDER.length;
const MONTHS_PER_YEAR = 12;

function getMonthName(month: string): string {
    return monthValues.find(name => name.toLowerCase().startsWith(month?.toLowerCase())) ?? month;
}

function dropPartialTrailingYear(cells: LaunchesChartCell[], periodsPerYear: number): LaunchesChartCell[] {
    const trailingYear = cells[cells.length - 1];
    if (!trailingYear || trailingYear.items.length >= periodsPerYear) return cells;

    let periodsToDrop = trailingYear.items.length;
    const result: LaunchesChartCell[] = [];
    cells.forEach(cell => {
        if (periodsToDrop >= cell.items.length) {
            periodsToDrop -= cell.items.length;
            return;
        }
        result.push(periodsToDrop > 0 ? { ...cell, items: cell.items.slice(periodsToDrop) } : cell);
        periodsToDrop = 0;
    });

    return result;
}

export function getCurrentSeason(): YearSeasonsEnum {
    const month = dayjs().month();
    if (month === 11 || month <= 1) return YearSeasonsEnum.Winter;
    if (month <= 4) return YearSeasonsEnum.Spring;
    if (month <= 7) return YearSeasonsEnum.Summer;
    return YearSeasonsEnum.Fall;
}

function groupRowsByYear(rows: LaunchesChartRow[], periodsPerYear: number): LaunchesChartCell[] {
    const cells: LaunchesChartCell[] = [];
    rows.forEach(({ year, ...bar }) => {
        const currentYear = cells[cells.length - 1];
        if (currentYear?.year === year) {
            currentYear.items.push(bar);
        } else {
            cells.push({ year, items: [bar] });
        }
    });

    return dropPartialTrailingYear(cells, periodsPerYear);
}

export function buildSeasonChart(bySeason: iLTOChart[]): LaunchesChartCell[] {
    const rows: LaunchesChartRow[] = bySeason.flatMap(({ year, quarters }) =>
        quarters
            .filter(({ season }) => LAUNCHES_SEASON_ORDER.includes(season))
            .map(({ season, value }) => ({ year, id: `${year}-${season}`, season, value })),
    );

    return groupRowsByYear(rows, SEASONS_PER_YEAR);
}

export function buildMonthChart(byMonth: LaunchActivityMonth[]): LaunchesChartCell[] {
    const rows: LaunchesChartRow[] = byMonth.map(({ year, month, value }, index) => ({
        year,
        id: `${year}-${index}`,
        value,
        month: getMonthName(month),
    }));

    return groupRowsByYear(rows, MONTHS_PER_YEAR);
}
